import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const emptyForm = { code: "", type: "percentage", value: "", min_order_amount: "0", usage_limit: "" };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [sale, setSale] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);

  const fetchCoupons = () => apiClient.get("/admin/coupons").then((res) => setCoupons(res.data));
  useEffect(() => {
    fetchCoupons();
    apiClient.get("/admin/sale-summary").then((res) => setSale(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await apiClient.post("/admin/coupons", {
      ...form,
      value: parseFloat(form.value),
      min_order_amount: parseFloat(form.min_order_amount || 0),
      usage_limit: form.usage_limit ? parseInt(form.usage_limit, 10) : null,
    });
    toast.success("Coupon created");
    setForm(emptyForm);
    setShowForm(false);
    fetchCoupons();
  };

  const toggleActive = async (c) => {
    await apiClient.put(`/admin/coupons/${c._id}`, { active: !c.active });
    fetchCoupons();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await apiClient.delete(`/admin/coupons/${id}`);
    toast.success("Coupon deleted");
    fetchCoupons();
  };

  return (
    <div data-testid="admin-coupons-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900">Coupons</h1>
        <Button size="sm" className="rounded-md" onClick={() => setShowForm(true)} data-testid="admin-new-coupon-btn"><Plus className="h-4 w-4 mr-1.5" /> New Coupon</Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-md shadow-sm p-5 mb-6 grid sm:grid-cols-2 gap-3" data-testid="admin-coupon-form">
          <div className="sm:col-span-2 flex justify-between"><p className="text-sm font-semibold text-slate-900">New Coupon</p><button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4 text-slate-400" /></button></div>
          <Input placeholder="Code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="coupon-form-code" />
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="coupon-form-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed">Fixed Amount</SelectItem><SelectItem value="free_shipping">Free Shipping</SelectItem></SelectContent>
          </Select>
          <Input type="number" placeholder="Value" required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="coupon-form-value" />
          <Input type="number" placeholder="Min Order Amount" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} data-testid="coupon-form-min-order" />
          <Input type="number" placeholder="Usage Limit (optional)" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} data-testid="coupon-form-usage-limit" />
          <Button type="submit" size="sm" className="rounded-md" data-testid="coupon-form-submit">Save Coupon</Button>
        </form>
      )}

      {sale && (
        <div className={`border rounded-md shadow-sm p-5 mb-6 flex flex-wrap items-center gap-x-8 gap-y-2 ${sale.active ? "bg-amber-50/60 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid="admin-sale-summary">
          <div>
            <p className="text-sm font-semibold text-slate-900">{sale.label} — Automatic Sitewide Sale</p>
            <p className="text-xs text-slate-500 mt-0.5">Applied automatically at checkout, no code needed. Doesn't stack with product coupons (FREESHIP stacks).</p>
          </div>
          <div className="text-sm">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${sale.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="admin-sale-status">{sale.active ? "Active" : "Ended"}</span>
          </div>
          <div className="text-sm text-slate-600" data-testid="admin-sale-ends">Ends {new Date(sale.ends_at).toLocaleDateString()}</div>
          <div className="text-sm text-slate-600" data-testid="admin-sale-used">Used on <span className="font-semibold text-slate-900">{sale.orders_used}</span> order{sale.orders_used === 1 ? "" : "s"}</div>
          <div className="text-sm text-slate-600" data-testid="admin-sale-discount-given">Total discount given: <span className="font-semibold text-slate-900">${sale.total_discount_given.toFixed(2)}</span></div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-slate-600">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Discount</th><th className="px-4 py-3">Used</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c._id} className="border-t border-slate-100" data-testid={`admin-coupon-row-${c.code}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{c.code}</td>
                <td className="px-4 py-3">{c.type === "percentage" ? `${c.value}%` : c.type === "free_shipping" ? "Free Shipping" : `$${c.value}`}</td>
                <td className="px-4 py-3">{c.used_count}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(c)} className={`text-xs px-2 py-1 rounded-full ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`admin-coupon-toggle-${c.code}`}>
                    {c.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => handleDelete(c._id)} data-testid={`admin-delete-coupon-${c.code}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {coupons.length === 0 && <p className="p-6 text-center text-slate-400 text-sm" data-testid="admin-no-coupons">No coupons yet.</p>}
      </div>
    </div>
  );
}
